import { useState, useCallback, useEffect, useRef } from "react";
import {
  useGoogleMapsAutocomplete,
  AddressData,
  AddressPrediction,
} from "@/hooks/useGoogleMapsAutocomplete";
import { debounce } from "@/utils/misc";

const DEBOUNCE_INTERVAL_MS = 300;
const MIN_SEARCH_LENGTH = 3;

export interface UseAddressAutocompleteReturn {
  addressPredictions: AddressPrediction[];
  loading: boolean;
  fetchAddressPredictions: (value: string) => void;
  addressInput: string;
  fetchAddressDetails: (addressId: string) => Promise<AddressData | null>;
  clearAddressPredictions: () => void;
}

/**
 * Generic Address Autocomplete Hook
 * Provider-agnostic - doesn't know we're using Google Maps
 * Manages UI state, debouncing, and service calls
 *
 * To switch providers: Only change the import and service method names
 */
export const useAddressAutocomplete = (): UseAddressAutocompleteReturn => {
  const [addressPredictions, setAddressPredictions] = useState<
    AddressPrediction[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");

  // Service layer - this is the only coupling to Google
  // To switch providers, change this import and adapt the method calls
  const {
    isLoaded,
    createAutocompleteSession,
    searchAddresses,
    fetchAddressDetails: fetchAddressDetailsFromService,
  } = useGoogleMapsAutocomplete();

  // Session management (provider might not need this, but Google does)
  const sessionRef = useRef<any>(null);
  const debouncedFetchRef = useRef<any>(null);

  /**
   * Starts a new search session
   * Some providers (like Google) use sessions for billing
   */
  const startNewSession = useCallback(async () => {
    sessionRef.current = await createAutocompleteSession();

    if (!sessionRef.current) {
      console.error("Failed to create new autocomplete session");
    }

    return sessionRef.current;
  }, [createAutocompleteSession]);

  /**
   * Initialize session when service loads
   */
  useEffect(() => {
    if (isLoaded && !sessionRef.current) {
      startNewSession();
    }
  }, [isLoaded, startNewSession]);

  /**
   * Core search logic
   */
  const executeFetch = useCallback(
    async (value: string) => {
      if (!value || value.trim().length < MIN_SEARCH_LENGTH) {
        setAddressPredictions([]);
        setLoading(false);
        setAddressInput(value);
        return;
      }

      if (!isLoaded) {
        console.error("Google API Service not loaded yet");
        setLoading(false);
        return;
      }

      setLoading(true);
      setAddressInput(value);

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const results = await searchAddresses(value, sessionRef.current);

        setAddressPredictions(results || []);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch address predictions:", error);
        setLoading(false);
        setAddressPredictions([]);
      }
    },
    [isLoaded, searchAddresses, startNewSession]
  );

  /**
   * Debounced search to prevent excessive API calls
   */
  const fetchAddressPredictions = useCallback(
    (value: string) => {
      if (debouncedFetchRef.current?.cancel) {
        debouncedFetchRef.current.cancel();
      }

      const debouncedFn = debounce((val: string) => {
        executeFetch(val);
      }, DEBOUNCE_INTERVAL_MS);

      debouncedFetchRef.current = debouncedFn;
      debouncedFn(value);
    },
    [executeFetch]
  );

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debouncedFetchRef.current?.cancel) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, []);

  /**
   * Fetches full details when user selects an address
   *
   * @param addressId - Provider-specific address identifier
   * @returns Full address data
   */
  const fetchAddressDetails = useCallback(
    async (addressId: string): Promise<AddressData | null> => {
      if (!addressId) {
        console.error("No addressId provided to fetchAddressDetails");
        return null;
      }

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const currentSession = sessionRef.current;

        // Start new session for next search cycle
        await startNewSession();

        // Fetch details using old session
        const addressData = await fetchAddressDetailsFromService(
          addressId,
          currentSession
        );

        return addressData;
      } catch (error) {
        console.error("Failed to fetch address details:", error);
        return null;
      }
    },
    [fetchAddressDetailsFromService, startNewSession]
  );

  /**
   * Clears search state
   */
  const clearAddressPredictions = useCallback(() => {
    setAddressPredictions([]);
    setAddressInput("");
    setLoading(false);
  }, []);

  return {
    addressPredictions,
    loading,
    fetchAddressPredictions,
    addressInput,
    fetchAddressDetails,
    clearAddressPredictions,
  };
};
